<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import { baseInput, baseLoading } from "@/common/components";

interface OptionsType {
	id: string;
	label: string;
	value: string;
}

const { t } = useI18n();

const emit = defineEmits(["search", "select", "clear"]);

const props = withDefaults(
	defineProps<{
		label?: string;
		placeholder?: string;
		search: string;
		searched?: boolean;
		loading?: boolean;
		disabled?: boolean;
		optionLabel: string;
		options?: any[];
	}>(),
	{
		optionLabel: "label",
	},
);

const searchInput = ref("");

function search() {
	// if (searchInput.value.trim().length === 0) {
	//   emit("clear");
	//   return;
	// }

	emit("search", searchInput.value);
}

function selectOption(option: OptionsType) {
	searchInput.value = option[props.optionLabel];

	emit("select", option);
}

const debounced = debounce(search, 600);

watch(
	() => props.search,
	() => {
		searchInput.value = props.search;
	},
);
</script>

<template>
	<section class="base-search">
		<base-input
			v-model="searchInput"
			:value="searchInput"
			:placeholder="props.placeholder"
			:disabled="disabled"
			@input="debounced"
		>
			<template v-if="props.label" #label>
				{{ props.label }}
			</template>
		</base-input>

		<template v-if="props.searched">
			<ul
				class="options shadow"
				:class="{
					empty: props.options.length === 0,
					loading: props.loading,
				}"
			>
				<template v-for="(option, index) in props.options" :key="index">
					<li @click="selectOption(option)">
						{{ option[props.optionLabel] }}
					</li>
				</template>

				<template v-if="props.options.length === 0">
					<li>
						{{ t("common.texts.no_results") }}
					</li>
				</template>

				<template v-if="props.loading">
					<li>
						<base-loading />
					</li>
				</template>
			</ul>
		</template>
	</section>
</template>

<style lang="scss" scoped>
.base-search {
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
  min-height: 300px;

	.options {
		position: absolute;
		z-index: 99;
		width: inherit;
		height: 10rem;
		margin-top: 4.5rem;
		overflow-y: auto;
		background: #ffffff;
		border: 1px solid #d9d9d9;
		border-radius: 0.25rem;
		list-style: none;
		padding: 0;

		li {
			cursor: pointer;
			padding-block: 0.75rem;
			padding-inline: 1rem;

			&:hover {
				background: var(--white-off);
			}
		}

		&.loading {
			height: 6rem;

			li {
				cursor: default;

				&:hover {
					background: transparent;
				}
			}
		}

		&.empty {
			height: 5rem;

			li {
				cursor: default;
				display: flex;
				flex-direction: column;
				justify-content: center;
				font-size: 0.875rem;
				padding-top: 1rem;
				color: var(--text-off);

				&:hover {
					background: transparent;
				}
			}
		}
	}
}
</style>
